import { QueryObserver, useQuery } from 'react-query'
import Cookies from 'cookies-js'

import queryClient from '../queryClient'
import isOnePlatformEnabled from '../isOnePlatformEnabled'
import { getBasenameFromPath } from '../basenameService'

import type { TokenResult } from './types'

const STORAGE_KEY = 'BEARER_TOKENS'

const defaultOptions = {
  retry: 300,
  retryInterval: 1000,
  refetchInterval: 300000,
  staleTime: 300000,
  refetchIntervalInBackground: true,
}

const isPublicVideoPreviewPage = () =>
  /\/personal\/\d+\/[a-f0-9A-F]{20,}/.test(window.location.pathname)

const doFetchTokens = async () => {
  if (
    isPublicVideoPreviewPage() ||
    (!isOnePlatformEnabled && getBasenameFromPath() !== 'personal')
  ) {
    return []
  }

  const requestHeaders = new Headers()
  const cookie = Cookies.get('_tt_dev_env')
  if (cookie) {
    requestHeaders.set('TwentyThree-Development-Environment', cookie)
  }

  const apiResponse = await fetch('/api/2/user/tokens?cross_sites_p=true', {
    credentials: 'include',
    headers: requestHeaders,
  })
  if (apiResponse.status >= 400 && apiResponse.status < 500) {
    throw new Error('Access denied')
  }

  if (apiResponse.status > 500) {
    throw new Error('API error')
  }
  const apiJsonResult = await apiResponse.json()
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(apiJsonResult.data))
  return apiJsonResult.data as TokenResult[]
}

const getCurrentSiteFromTokens = (tokens: TokenResult[] = []) => {
  const site = getBasenameFromPath()

  if (site === 'home') {
    return tokens[0]
  }
  // What happens if we have tokens, but don't find the site key?
  // Should we redirect the user to "Home" or just pick a workspace for them?
  return tokens.find(({ site_key }) => site_key === site)
}

// eslint-disable-next-line import-x/no-default-export
export default class TokenService {
  private storage: Storage

  private storageKey: string = 'BEARER_TOKENS'

  static instance: TokenService

  constructor(storage?: Storage, storageKey?: string) {
    this.storage = !storage ? window.sessionStorage : storage
    this.storageKey = !storageKey ? this.storageKey : storageKey
  }

  getTokens(): Array<TokenResult> {
    const tokens = queryClient.getQueryData<TokenResult[]>('tokens')
    if (tokens && tokens.length > 0) {
      return tokens
    }
    const data = this.storage.getItem(this.storageKey)
    if (data) {
      const tokensFromStorage = JSON.parse(data) as Array<TokenResult>
      return tokensFromStorage
    }
    void this.fetchTokens()
    return queryClient.getQueryData<TokenResult[]>('tokens') || []
  }

  getCurrentSiteToken() {
    const tokenList = this.getTokens()
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!tokenList || tokenList.length === 0) {
      console.error('No tokens found')
      return undefined
    }
    return getCurrentSiteFromTokens(tokenList)
  }

  fetchTokens = async () => {
    const result = await queryClient.fetchQuery<TokenResult[], Error>(
      'tokens',
      doFetchTokens,
      defaultOptions
    )
    this.storage.setItem(this.storageKey, JSON.stringify(result))
    return result
  }

  clearTokens() {
    void queryClient.invalidateQueries('tokens')
    this.storage.removeItem(this.storageKey)
  }

  getTokenFromUrl(url: string): TokenResult | null {
    return this.getTokens().find(({ entry_url }) => entry_url === url) ?? null
  }

  static getInstance(): TokenService {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!TokenService.instance) {
      TokenService.instance = new TokenService()
    }
    return TokenService.instance
  }
}

export const useTokens = () => {
  return useQuery<TokenResult[], Error>('tokens', doFetchTokens, defaultOptions)
}

export const useCurrentSiteToken = () => {
  const result = useTokens()
  return {
    ...result,
    data: getCurrentSiteFromTokens(result.data),
  }
}

const observer = new QueryObserver<TokenResult[], Error>(queryClient, {
  queryKey: 'tokens',
  queryFn: doFetchTokens,
  ...defaultOptions,
})

observer.subscribe((result) => {
  if (!isOnePlatformEnabled) {
    return
  }
  if (result.isError && result.error.message === 'Access denied') {
    TokenService.getInstance().clearTokens()
    window.location.href = '/logout'
    return
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result.data))
})
